import {useFrame} from '@react-three/fiber';
import {useEffect, useState} from 'react';
import {TrainingTarget} from '../../components/app/TrainingTarget';
import {
  VR_POSITIONS,
  createPeripheralPositions,
  createTargetPosition,
  getDistanceValue,
  getSpeedValue,
} from '../../constants/vrPositions';
import {useExerciseTime} from '../../hooks/useExerciseTime';
import {ballPositionStore, useXRController} from '../../hooks/useXRController';
import {useStore} from '../../store/useStore';
import {DistanceSetting, ExerciseSettings, RangeSetting} from '../../types/exercises';
import {logger} from '../../utils/logger';

export const ConvergenceExercise: React.FC<ExerciseSettings> = (props) => {
  const {
    eyeToTrain = 'both',
    isRedGreen = false,
    isMFBF = false,
    isOverlay = false,
    leftEyeColor,
    rightEyeColor,
    leftEyeOverlay,
    rightEyeOverlay,
    speed: initialSpeed = 7,
    range: initialRange = 'medium',
    distance = 'middle',
    ballPosition,
    onEndExercise,
  } = props;

  // Local state for settings that can be changed in VR
  const [speed, setSpeed] = useState(initialSpeed);
  const [range, setRange] = useState(initialRange);
  const [duration, setDuration] = useState(45); // Default duration
  const baseDistance = getDistanceValue(distance);
  const initialPosition = createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, baseDistance - 1); // Start a bit further out

  const [targetPosition, setTargetPosition] = useState(initialPosition);
  const [isInitialized, setIsInitialized] = useState(false);
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [isPaused, setIsPaused] = useState(false);

  // Use the new hook to get time remaining
  const timeRemaining = useExerciseTime();

  // Get the global timer pause action from Zustand store
  const toggleTimerPause = useStore(state => state.toggleTimerPause);

  // Use the centralized XR controller hook
  const {isInXR, handlePositionChange} = useXRController();

  // Handle menu toggle - update local pause state AND global timer pause state
  const onMenuToggle = (isOpen: boolean) => {
    setIsPaused(isOpen);
    toggleTimerPause(isOpen);
    logger.info(`ConvergenceExercise: Menu toggled, global pause set to ${isOpen}`);
  };

  // Handle ending exercise immediately
  const handleEndExercise = () => {
    logger.info('ConvergenceExercise: End exercise requested');
    if (onEndExercise) {
      onEndExercise();
    }
  };


  // Get range points specifically for convergence exercise
  const getRangePoints = (rangeSetting: RangeSetting, distanceSetting: DistanceSetting): [number, number] => {
    // Base distance value serves as the center point
    const baseDistance = getDistanceValue(distanceSetting);

    // How far from the base distance to move (in both directions)
    let rangeValue: number;
    switch (rangeSetting) {
      case 'narrow':
        rangeValue = 0.8;
        break;
      case 'medium':
        rangeValue = 1.2;
        break;
      case 'wide':
        rangeValue = 1.8;
        break;
      default:
        rangeValue = 1.2;
    }

    // For convergence, we want to move from far to near
    const farPoint = baseDistance - rangeValue;
    const nearPoint = baseDistance + rangeValue;

    return [farPoint, nearPoint];
  };

  // Apply settings to movement parameters - recalculate when settings change
  const movementSpeed = getSpeedValue(speed);
  const [startZ, endZ] = getRangePoints(range, distance);
  const [leftPeripheral, rightPeripheral] = createPeripheralPositions(baseDistance);

  // Initialize ball position from saved settings
  useEffect(() => {
    if (ballPosition) {
      logger.info('Initializing ball position from saved settings:', ballPosition);
      ballPositionStore.setOffset(ballPosition);
    }
  }, [ballPosition]);

  // Initialize with the far point and reset when range changes
  useEffect(() => {
    logger.info('ConvergenceExercise mounted or range changed', {range, speed});
    setTargetPosition(createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, startZ));
    setIsInitialized(true);
    setDirection('in'); // Reset direction when range changes
    return () => {
      logger.info('ConvergenceExercise unmounted');
    };
  }, [startZ, range, speed]);

  useFrame((_, delta) => {
    // Skip updates if not initialized or paused
    if (!isInitialized || isPaused) {
      return;
    }

    const currentZ = targetPosition.z;

    if (direction === 'in') {
      const newZ = currentZ + movementSpeed * delta;
      if (newZ >= endZ) {
        setDirection('out');
      } else {
        setTargetPosition(createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, newZ));
      }
    } else {
      const newZ = currentZ - movementSpeed * delta;
      if (newZ <= startZ) {
        setDirection('in');
      } else {
        setTargetPosition(createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, newZ));
      }
    }
  });

  return (
    <group>
      {/* Main training target */}
      <TrainingTarget
        position={targetPosition}
        showPeripheral={false}
        eyeToTrain={eyeToTrain}
        isRedGreen={isRedGreen}
        isMFBF={isMFBF}
        isOverlay={isOverlay}
        leftEyeColor={leftEyeColor}
        rightEyeColor={rightEyeColor}
        leftEyeOverlay={leftEyeOverlay}
        rightEyeOverlay={rightEyeOverlay}
        inXRSession={isInXR}
        onPositionChange={handlePositionChange}
        onMenuToggle={onMenuToggle}
        onEndExercise={handleEndExercise}
        timeRemaining={timeRemaining}
        onSpeedChange={setSpeed}
        onRangeChange={setRange}
        onDurationChange={setDuration}
        currentSpeed={speed}
        currentRange={range}
        currentDuration={duration}
      />

      {/* MFBF peripheral awareness targets */}
      {isMFBF && (
        <>
          <TrainingTarget
            position={leftPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
          <TrainingTarget
            position={rightPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
        </>
      )}
    </group>
  );
};
